import React, { useEffect, useState } from 'react';
import { Autocomplete, Checkbox, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { CloseIcon } from '../../helper/Icons';
import { Button, ErrorMessage, Input, Select } from '../../components/Common';
import { useGetCityListQuery, useGetStateListQuery } from '../../services/Location';
import { useMessage } from '../../components/Common/Message/MessageContext';
import { addressTypeList, countryList } from '../../helper';
import { useAddUserAddressMutation, useUpdateAddressMutation } from '../../services/Address';
import { useGetPincodeBySubAdminQuery } from '../../services/SubAdmin';
import { getAllData } from '../../helper/indexDB';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AddAddress = ({ handleClose, modalOpen, onAddressAdded }) => {
    const { showMessage } = useMessage();
    const [country, setCountry] = useState(1);
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [user, setUser] = useState('');

    const [addressType, setAddressType] = useState('');
    const [errorMessages, setErrorMessages] = useState({});
    const [loading, setLoading] = useState(false);
    // Store pincodes as array; in edit mode we'll keep a single value in the array
    const [selectedPincodes, setSelectedPincodes] = useState([]);
    const [formValues, setFormValues] = useState({
        house: '',
        building: '',
        address: '',
        landmark: '',
        latitude: '',
        longitude: '',
    });

    const { data: stateLst, error: stateError, isLoading: stateLoading } = useGetStateListQuery();
    const { data: cityLst, error: cityError, isLoading: cityLoading } = useGetCityListQuery(state, { skip: !state });

    const cityList = cityLst?.data;
    const stateList = stateLst?.data;

    const { data: pincodeLst, error: pincodeError, isLoading: pincodeLoading } = useGetPincodeBySubAdminQuery(user, { skip: !user });
    const pincodeList = pincodeLst?.pinCodeData;

    const handleStateChange = (e) => {
        setState(e.target.value);
    };

    useEffect(() => {
        const fetchData = async () => {
            const storedData = await getAllData();
            setUser(storedData?.[0]?.value.id);
        };

        fetchData();
    }, []);

    const handleCountryChange = (e) => setCountry(e.target.value);
    const handleCityChange = (e) => setCity(e.target.value);
    const handleAddressTypeChange = (e) => setAddressType(e.target.value);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };

    const validateForm = () => {
        let errors = {};
        let hasError = false;

        Object.keys(formValues).forEach(key => {
            if (!formValues[key]) {
                errors[key] = `${key.charAt(0).toUpperCase() + key.slice(1)} is required`;
                hasError = true;
            }
        });

        if (!addressType) {
            errors.addressType = 'Address Type is required';
            hasError = true;
        }
        if (!modalOpen?.isEdit && !country) {
            errors.country = 'Country is required';
            hasError = true;
        }
        if (!modalOpen?.isEdit && !state) {
            errors.state = 'State is required';
            hasError = true;
        }
        if (!modalOpen?.isEdit && !city) {
            errors.city = 'City is required';
            hasError = true;
        }
        if (modalOpen?.isEdit) {
            const pinValue = Array.isArray(selectedPincodes) ? selectedPincodes[0] : selectedPincodes;
            if (!pinValue) {
                errors.pincode = 'Please select a pincode';
                hasError = true;
            }
        } else {
            if (!selectedPincodes || selectedPincodes.length === 0) {
                errors.pincode = 'Please select at least one pincode';
                hasError = true;
            }
        }

        setErrorMessages(errors);
        return !hasError;
    };

    const [addAddress] = useAddUserAddressMutation();
    const [updateAddress] = useUpdateAddressMutation();

    const handleSubmit = async () => {
        if (validateForm()) {
            setLoading(true);
            try {
                let response;

                const pinValue = Array.isArray(selectedPincodes) ? selectedPincodes[0] : selectedPincodes;

                const addressData = {
                    ...formValues,
                    cityId: city.toString(),
                    state: state.toString(),
                    type: addressType,
                    userId: modalOpen?.userId,
                    pincode: JSON.stringify(selectedPincodes), // JSON array in add mode
                };
                const updatAddressData = {
                    ...formValues,
                    type: addressType,
                    userId: modalOpen?.userId,
                    pincode: pinValue, // single value in edit mode
                };

                if (modalOpen.isEdit) {
                    response = await updateAddress({ body: updatAddressData, id: modalOpen?.addressData?.addressId, jobId: modalOpen?.jobId });
                } else {
                    response = await addAddress(addressData);
                }

                const { status, message } = response?.data;

                if (status) {
                    handleClose();
                    showMessage('success', message);
                    if (onAddressAdded) {
                        onAddressAdded();
                    }
                } else {
                    showMessage('error', message || 'An error occurred');
                }
            } catch (error) {
                showMessage('error', 'Failed to save the address');
            } finally {
                setLoading(false);
            }
        } else {
            showMessage('error', 'Please fill in all required fields');
        }
    };

    useEffect(() => {
        if (modalOpen?.addressData) {
            const { address, buildingName, house, lat, long, landmark, zip } = modalOpen.addressData;
            setFormValues({ address, building: buildingName, house, latitude: lat, longitude: long, landmark });
            setCity(modalOpen.addressData.city || '');
            setAddressType(modalOpen.addressData.addressType || '');
            setCountry(modalOpen.addressData.country || 1);
            setState(modalOpen?.addressData?.state || '');
            
            // Handle pincode - could be string, array, or object
            if (zip) {
                try {
                    const parsedZip = typeof zip === 'string' ? JSON.parse(zip) : zip;
                    setSelectedPincodes(Array.isArray(parsedZip) ? parsedZip : [parsedZip]);
                } catch {
                    setSelectedPincodes([zip]);
                }
            } else {
                setSelectedPincodes([]);
            }
        } else {
            setFormValues({
                house: '',
                building: '',
                address: '',
                landmark: '',
                latitude: '',
                longitude: '',
            });
            setCity('');
            setState('');
            setAddressType('');
            setCountry(1);
            setSelectedPincodes([]);
        }
    }, [modalOpen?.addressData]);

    return (
        <>
            <DialogTitle sx={{ m: 0, p: 2 }} id="Common_modal">
                {modalOpen.isEdit ? 'Edit' : "Add"} Address
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <Grid container spacing={2} width={{ sm: '400px' }} paddingY={1}>
                    <Grid item xs={12} sm={6}>
                        <Input labelinput='House No.' name='house' value={formValues.house} onChange={handleInputChange} />
                        {errorMessages.house && <ErrorMessage message={errorMessages.house} />}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Input labelinput='Building Name' name='building' value={formValues.building} onChange={handleInputChange} />
                        {errorMessages.building && <ErrorMessage message={errorMessages.building} />}
                    </Grid>
                    <Grid item xs={12}>
                        <Input labelinput='Address' name='address' value={formValues.address} onChange={handleInputChange} />
                        {errorMessages.address && <ErrorMessage message={errorMessages.address} />}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Input labelinput='Landmark' name='landmark' value={formValues.landmark} onChange={handleInputChange} />
                        {errorMessages.landmark && <ErrorMessage message={errorMessages.landmark} />}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Select selectList={addressTypeList} labelinput='Address Type' value={addressType} onChange={handleAddressTypeChange} />
                        {errorMessages.addressType && <ErrorMessage message={errorMessages.addressType} />}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>
                            {modalOpen?.isEdit ? 'Select Pincode' : 'Select Pincodes'}
                        </Typography>
                        <Autocomplete
                            multiple={!modalOpen?.isEdit}
                            id="pincode-select"
                            options={pincodeLoading ? [] : pincodeList || []}
                            disableCloseOnSelect={!modalOpen?.isEdit}
                            getOptionLabel={(option) => option.pincode || option}
                            isOptionEqualToValue={(option, value) => {
                                const optionValue = typeof option === 'object' ? option.pincode : option;
                                const valueValue = typeof value === 'object' ? value.pincode : value;
                                return optionValue === valueValue;
                            }}
                            value={modalOpen?.isEdit
                                ? (() => {
                                    if (!pincodeList || !selectedPincodes || selectedPincodes.length === 0) return null;
                                    const target = Array.isArray(selectedPincodes) ? selectedPincodes[0] : selectedPincodes;
                                    return (pincodeList || []).find(p => {
                                        const pincodeValue = typeof p === 'object' ? p.pincode : p;
                                        return pincodeValue === target;
                                    }) || null;
                                })()
                                : (selectedPincodes || []).map(p => (pincodeList || []).find(opt => {
                                    const optValue = typeof opt === 'object' ? opt.pincode : opt;
                                    return optValue === p;
                                })).filter(Boolean)
                            }
                            onChange={(event, newValue) => {
                                if (modalOpen?.isEdit) {
                                    const pin = newValue ? (typeof newValue === 'object' ? newValue.pincode : newValue) : '';
                                    setSelectedPincodes(pin ? [pin] : []);
                                } else {
                                    const formattedPincodes = newValue.map(item =>
                                        typeof item === 'object' ? item.pincode : item
                                    );
                                    setSelectedPincodes(formattedPincodes);
                                }
                                setErrorMessages(prev => ({ ...prev, pincode: '' }));
                            }}
                            renderOption={modalOpen?.isEdit ? undefined : (props, option, { selected }) => {
                                const { key, ...optionProps } = props;
                                const pincodeValue = typeof option === 'object' ? option.pincode : option;
                                return (
                                    <li key={key} {...optionProps}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {pincodeValue}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={modalOpen?.isEdit ? "Select pincode" : "Select pincodes"}
                                />
                            )}
                        />
                        {errorMessages.pincode && <ErrorMessage message={errorMessages.pincode} />}
                    </Grid>
                    <Grid item xs={12}>
                        <Select disabled={modalOpen?.isEdit} selectList={countryList} labelinput='Country' value={country} onChange={handleCountryChange} />
                        {errorMessages.country && <ErrorMessage message={errorMessages.country} />}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {modalOpen?.isEdit ?
                            <Input labelinput='State' value={state} disabled={modalOpen?.isEdit} />
                            :
                            <>
                                <Select selectList={stateLoading ? [] : stateList} labelinput='State' value={state} onChange={handleStateChange} />
                                {errorMessages.state && <ErrorMessage message={errorMessages.state} />}
                            </>
                        }
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        {modalOpen?.isEdit ?
                            <Input labelinput='City' value={city} disabled={modalOpen?.isEdit} />
                            :
                            <>
                                <Select selectList={cityLoading ? [] : cityList} labelinput='City' value={city} onChange={handleCityChange} />
                                {errorMessages.city && <ErrorMessage message={errorMessages.city} />}
                            </>
                        }
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Input labelinput='Latitude' type='number' name='latitude' value={formValues.latitude} onChange={handleInputChange} />
                        {errorMessages.latitude && <ErrorMessage message={errorMessages.latitude} />}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Input labelinput='Longitude' type='number' name='longitude' value={formValues.longitude} onChange={handleInputChange} />
                        {errorMessages.longitude && <ErrorMessage message={errorMessages.longitude} />}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button buttonName={'Cancel'} size='small' color='white' onClick={handleClose} disabled={loading} />
                <Button buttonName={modalOpen.isEdit ? 'Update' : 'Save'} size='small' onClick={handleSubmit} disabled={loading} />
            </DialogActions>
        </>
    );
};

export default AddAddress;